<script lang="ts">
	import { createQuery } from '@tanstack/svelte-query'
	import DetailedSelect from './DetailedSelect.svelte'
	import {
		DetailedSelectItemableElementType,
		type EmbeddingModelSelectable
	} from '@repo/commons/models'
	import { elementToDetailedSelectItem } from '@repo/commons/utils'
	import type { DetailedSelectItem } from '@repo/commons/models'
	import type { QueryKey } from '@tanstack/svelte-query'

	interface Props {
		queryKey: QueryKey
		fetcher: () => Promise<{ embeddingModels: EmbeddingModelSelectable[] }>
		enabled?: boolean
		selectedItem?: DetailedSelectItem | null
		placeholder?: string
		position?: 'left' | 'right'
		label?: string
		error?: string
		isRequired?: boolean
		disabled?: boolean
		isPending?: boolean
		isError?: boolean
		prefixIcon?: { name: string; class?: string; size?: string } | null
		onSelect?: ((item: DetailedSelectItem | null) => void) | null
		onItemsChange?: ((items: DetailedSelectItem[]) => void) | null
	}

	let {
		queryKey,
		fetcher,
		enabled = true,
		selectedItem = null,
		placeholder = 'Select embedding model',
		position = 'left',
		label = '',
		error = '',
		isRequired = false,
		disabled = false,
		isPending = $bindable(false),
		isError = $bindable(false),
		prefixIcon = { name: 'openai', size: 'lg' },
		onSelect = null,
		onItemsChange = null
	}: Props = $props()

	const embeddingModelsQuery = $derived(
		createQuery({
			queryKey,
			queryFn: () => fetcher(),
			enabled
		})
	)

	const allModels = $derived($embeddingModelsQuery.data?.embeddingModels ?? [])

	const items = $derived(
		allModels.map((m) =>
			elementToDetailedSelectItem(m, DetailedSelectItemableElementType.EMBEDDING_MODEL)
		)
	)

	const resolvedSelectedItem = $derived.by((): DetailedSelectItem | null => {
		if (!selectedItem) return null
		return items.find((item) => item.title === selectedItem.title) ?? selectedItem
	})

	$effect(() => {
		isPending = $embeddingModelsQuery.isPending
	})

	$effect(() => {
		isError = $embeddingModelsQuery.isError
	})

	$effect(() => {
		onItemsChange?.(items)
	})
</script>

<DetailedSelect
	{items}
	selectedItem={resolvedSelectedItem}
	{placeholder}
	{position}
	{label}
	{error}
	{isRequired}
	searchable={false}
	disabled={disabled || $embeddingModelsQuery.isPending}
	isLoading={$embeddingModelsQuery.isPending}
	{onSelect}
	{prefixIcon}
	suffixIcon={resolvedSelectedItem?.endIcon ?? null}
/>
